import { dashboardLocationColor } from '@/lib/dashboardColors'
// Mantine
import { Anchor, Button, RingProgress } from '@mantine/core'

// Router
import RouterLink from '@/components/ui/RouterLink'

// Icons
import {
    IconCheck,
    IconAlertTriangle,
    IconPackage,
    IconChevronRight,
} from '@tabler/icons-react'

// UI
import Box from '../ui/Box'

// Number flow
import NumberFlow from '@number-flow/react'

// Dayjs
import dayjs from 'dayjs'

export default function DashboardFulfillmentOrdersWaiting({
    fulfillment_stats_by_location,
    fulfillments_orders_address_errors,
    last_fetch,
}) {
    // Get total
    let total = 0

    fulfillment_stats_by_location?.forEach((location) => {
        total += location.fulfillments_orders_to_fulfill
    })

    const actionButton = (
        <Button
            component={RouterLink}
            href="/fulfillment_orders?location_id=all&fulfill_at=3_days_or_before"
            variant="default"
            size="xs"
            rightSection={<IconChevronRight size={14} />}
            className="text-xs font-medium"
        >
            Voir tout
        </Button>
    )

    return (
        <Box title="Demandes à préparer" rightSection={actionButton}>
            <div className="space-y-6">
                {/* Critical address errors - prominent alert */}
                {fulfillments_orders_address_errors > 0 && (
                    <RouterLink
                        href="/fulfillment_orders?location_id=all&fulfill_at=all&verify_address_error=true"
                        className="group flex items-center justify-between p-4 group overflow-hidden rounded-xl bg-red-50 hover:bg-red-100/60 border border-red-200/80 shadow-xs"
                    >
                        <div className="flex items-start gap-3">
                            <div className="shrink-0 mt-0.5">
                                <div className="p-1.5 bg-red-100 rounded-lg">
                                    <IconAlertTriangle
                                        size={16}
                                        className="text-red-600"
                                    />
                                </div>
                            </div>

                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-red-900">
                                    Action requise
                                </div>
                                <div className="text-sm text-red-700 mt-0.5">
                                    {fulfillments_orders_address_errors}{' '}
                                    {fulfillments_orders_address_errors === 1
                                        ? 'adresse invalide'
                                        : 'adresses invalides'}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 text-red-600 transition-colors">
                            <span className="text-xs font-medium">
                                Corriger
                            </span>
                            <IconChevronRight
                                size={14}
                                className="group-hover:translate-x-0.5 transition-transform"
                            />
                        </div>
                    </RouterLink>
                )}

                {/* Hero metrics section */}
                {fulfillment_stats_by_location?.length > 0 && (
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <div className="p-3 bg-blue-50 rounded-xl border border-blue-300">
                                    <IconPackage
                                        size={20}
                                        className="text-blue-600"
                                    />
                                </div>

                                {total > 0 && (
                                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
                                )}
                            </div>

                            <div className="flex flex-col">
                                <span className="text-3xl font-bold text-gray-900 tracking-tight">
                                    <NumberFlow value={total} />
                                </span>

                                <div className="text-sm text-gray-600">
                                    Préparations à faire
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loading skeleton */}
                {!fulfillment_stats_by_location && (
                    <>
                        {/* Hero metrics skeleton */}
                        <div className="flex items-center gap-4">
                            <div className="w-[44px] h-[44px] bg-gray-200 rounded-xl animate-pulse" />
                            <div>
                                <div className="flex items-baseline gap-2">
                                    <div className="w-10 h-9 bg-gray-200 rounded-sm animate-pulse" />
                                </div>
                                <div className="w-32 h-5 bg-gray-100 rounded-sm animate-pulse mt-0.5" />
                            </div>
                        </div>

                        {/* Location cards skeleton */}
                        <div className="space-y-2">
                            <div className="w-24 h-3 bg-gray-200 rounded-sm animate-pulse mb-3" />
                            {[1].map((i) => (
                                <div
                                    key={i}
                                    className="relative overflow-hidden rounded-lg border border-gray-200 bg-white p-4"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-[45px] h-[45px] bg-gray-200 rounded-full animate-pulse" />
                                            <div className="flex flex-col gap-1.5">
                                                <div className="w-24 h-4 bg-gray-200 rounded-sm animate-pulse" />
                                                <div className="w-20 h-3 bg-gray-100 rounded-sm animate-pulse" />
                                                <div className="w-16 h-3 bg-gray-100 rounded-sm animate-pulse" />
                                            </div>
                                        </div>
                                        <div className="w-24 h-6 bg-gray-200 rounded-full animate-pulse" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* Perfect state - all completed */}
                {fulfillment_stats_by_location?.length === 0 && (
                    <div className="text-center py-10">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-linear-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-100 mb-4">
                            <IconCheck size={28} className="text-green-600" />
                        </div>

                        <div className="space-y-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Parfait ! Tout est à jour
                            </h3>

                            <p className="text-sm text-gray-600 max-w-sm mx-auto">
                                Toutes les demandes de préparation ont été
                                traitées.
                            </p>
                        </div>
                    </div>
                )}

                {/* Location cards */}
                {fulfillment_stats_by_location?.length > 0 && (
                    <div className="space-y-2">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                            Par emplacement
                        </div>

                        {fulfillment_stats_by_location.map(
                            (location, index) => {
                                const total_for_the_day =
                                    location.fulfillments_orders_to_fulfill +
                                    location.fulfillments_done_today

                                const progress = Math.round(
                                    (location.fulfillments_done_today /
                                        total_for_the_day) *
                                        100
                                )

                                return (
                                    <RouterLink
                                        key={index}
                                        href={`/fulfillment_orders?location_id=${location.id}&fulfill_at=3_days_or_before`}
                                        className="group block"
                                    >
                                        <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-white transition-all duration-200 hover:bg-gray-50">
                                            <div className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <RingProgress
                                                                size={45}
                                                                thickness={7}
                                                                transitionDuration={
                                                                    1000
                                                                }
                                                                sections={[
                                                                    {
                                                                        value:
                                                                            100 -
                                                                            progress,
                                                                        color: dashboardLocationColor(location.id),
                                                                    },
                                                                    {
                                                                        value: progress,
                                                                        color: 'green',
                                                                    },
                                                                ]}
                                                            />
                                                        </div>

                                                        <div className="min-w-0">
                                                            <div className="font-medium text-gray-900 text-sm transition-colors">
                                                                {location.name}
                                                            </div>

                                                            <div className="flex flex-col gap-0.5 mt-0.5 font-medium">
                                                                <div className="text-xs" style={{ color: dashboardLocationColor(location.id) }}>
                                                                    <NumberFlow
                                                                        value={
                                                                            location.fulfillments_orders_to_fulfill
                                                                        }
                                                                        suffix=" à préparer"
                                                                    />
                                                                </div>

                                                                <div className="text-xs text-green-500">
                                                                    <NumberFlow
                                                                        value={
                                                                            location.fulfillments_done_today
                                                                        }
                                                                        suffix=" faite(s)"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-10">
                                                        <div className="flex items-center gap-2">
                                                            <div
                                                                className={`px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-600 border border-blue-300`}
                                                            >
                                                                <NumberFlow
                                                                    value={
                                                                        location.fulfillments_orders_to_fulfill
                                                                    }
                                                                    suffix=" à préparer"
                                                                />
                                                            </div>
                                                            <IconChevronRight
                                                                size={14}
                                                                className="text-gray-400 group-hover:translate-x-0.5 transition-all"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </RouterLink>
                                )
                            }
                        )}
                    </div>
                )}

                {/* Address validation success - subtle confirmation */}
                {fulfillments_orders_address_errors === 0 && total > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                        <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center">
                            <IconCheck size={10} className="text-green-600" />
                        </div>
                        <span className="text-xs text-green-700 font-medium">
                            Toutes les adresses sont validées
                        </span>
                    </div>
                )}

                {last_fetch && (
                    <div className="text-xs text-gray-400 text-center">
                        {dayjs(last_fetch).format('À HH:mm:ss')}
                    </div>
                )}
            </div>
        </Box>
    )
}
